import React, { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";

import { apiRequest } from "@mhj/shared/api";
import { LG_NAMES } from "../lib/lga";
import { CATEGORY_LABELS } from "../lib/scholarship";
import type { ScholarshipCategory } from "@mhj/shared/types";

const CATEGORIES: ScholarshipCategory[] = [
  "jamb",
  "undergraduate",
  "postgraduate",
  "others",
];

type ScholarshipConfig = {
  enabledCategories: string[];
  allowedLgas: string[] | null;
};

export default function AdminScholarshipSettings() {
  const queryClient = useQueryClient();
  const [enabledCategories, setEnabledCategories] = useState<string[]>([]);
  const [restrictByLga, setRestrictByLga] = useState(false);
  const [selectedLgas, setSelectedLgas] = useState<string[]>([]);

  const { data, isLoading } = useQuery<ScholarshipConfig>({
    queryKey: ["admin", "scholarship-settings"],
    queryFn: () =>
      apiRequest<ScholarshipConfig>("/admin/scholarship-settings"),
  });

  useEffect(() => {
    if (data) {
      setEnabledCategories(data.enabledCategories);
      setRestrictByLga(data.allowedLgas !== null && data.allowedLgas.length > 0);
      setSelectedLgas(data.allowedLgas ?? [])
    }
  }, [data]);

  const updateMutation = useMutation({
    mutationFn: (payload: ScholarshipConfig) =>
      apiRequest<ScholarshipConfig>("/admin/scholarship-settings", {
        method: "PUT",
        data: payload,
        withCsrf: true,
      }),
    onSuccess: (updated) => {
      queryClient.setQueryData(["admin", "scholarship-settings"], updated);
      queryClient.invalidateQueries({ queryKey: ["scholarship-config"] });
      toast.success("Settings saved.");
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });

  const handleToggleCategory = (category: string) => {
    setEnabledCategories((prev) =>
      prev.includes(category)
        ? prev.filter((c) => c !== category)
        : [...prev, category],
    );
  };

  const handleToggleLga = (lga: string) => {
    setSelectedLgas((prev) =>
      prev.includes(lga) ? prev.filter((l) => l !== lga) : [...prev, lga],
    );
  };

  const handleSelectAllLgas = () => {
    setSelectedLgas([...LG_NAMES]);
  };

  const handleClearLgas = () => {
    setSelectedLgas([]);
  };

  const handleSave = () => {
    const allowedLgas = restrictByLga
      ? selectedLgas.length > 0
        ? selectedLgas
        : null
      : null;
    if (restrictByLga && selectedLgas.length === 0) {
      toast.error("Select at least one LGA when restricting by LGA.");
      return;
    }
    // if (enabledCategories.length === 0) {
    //   toast.error("Enable at least one scholarship category.");
    //   return;
    // }
    updateMutation.mutate({
      enabledCategories,
      allowedLgas,
    });
  };

  if (isLoading && !data) {
    return (
      <div className="glass-card rounded-3xl p-6">
        <p className="text-sm text-slate-500">Loading settings…</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="glass-card rounded-3xl p-6">
        <h2 className="text-lg font-semibold text-slate-900">
          Scholarship application settings
        </h2>
        <p className="mt-1 text-sm text-slate-600">
          Control which categories are open for applications and optionally restrict by LGA.
        </p>

        <div className="mt-6">
          <p className="text-xs font-medium uppercase tracking-wider text-slate-500">
            Enabled categories
          </p>
          <p className="mt-1 text-sm text-slate-600">
            Applicants can only start or save drafts for enabled categories.
          </p>
          <div className="mt-3 flex flex-wrap gap-4">
            {CATEGORIES.map((cat) => (
              <label
                key={cat}
                className="flex cursor-pointer items-center gap-2 rounded-xl border border-sky-200 bg-white px-4 py-3 transition hover:border-sky-300"
              >
                <input
                  type="checkbox"
                  checked={enabledCategories.includes(cat)}
                  onChange={() => handleToggleCategory(cat)}
                  className="h-4 w-4 rounded border-sky-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-slate-700">
                  {CATEGORY_LABELS[cat]}
                </span>
              </label>
            ))}
          </div>
        </div>

        <div className="mt-8">
          <label className="flex cursor-pointer items-center gap-2">
            <input
              type="checkbox"
              checked={restrictByLga}
              onChange={(e) => setRestrictByLga(e.target.checked)}
              className="h-4 w-4 rounded border-sky-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm font-medium text-slate-700">
              Restrict applications by LGA
            </span>
          </label>
          <p className="mt-1 text-sm text-slate-600">
            When enabled, only applicants from selected LGAs can apply. When disabled, all Niger State LGAs are allowed.
          </p>

          {restrictByLga && (
            <div className="mt-4 rounded-2xl border border-sky-200 bg-sky-50/50 p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-xs font-medium uppercase tracking-wider text-slate-500">
                  Allowed LGAs ({selectedLgas.length})
                </p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleSelectAllLgas}
                    className="rounded-full border border-sky-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 transition hover:bg-sky-100"
                  >
                    Select all
                  </button>
                  <button
                    type="button"
                    onClick={handleClearLgas}
                    className="rounded-full border border-sky-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 transition hover:bg-sky-100"
                  >
                    Clear
                  </button>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
                {LG_NAMES.map((lga) => (
                  <label
                    key={lga}
                    className="flex cursor-pointer items-center gap-2 rounded-lg border border-sky-100 bg-white px-3 py-2 transition hover:border-sky-200"
                  >
                    <input
                      type="checkbox"
                      checked={selectedLgas.includes(lga)}
                      onChange={() => handleToggleLga(lga)}
                      className="h-3.5 w-3.5 rounded border-sky-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-xs text-slate-700">{lga}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <button
            type="button"
            onClick={handleSave}
            disabled={updateMutation.isPending}
            className="rounded-full bg-blue-600 px-5 py-2.5 text-sm font-medium text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {updateMutation.isPending ? "Saving…" : "Save settings"}
          </button>
        </div>
      </div>
    </div>
  );
}
